import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ExpandMore } from "@mui/icons-material";
import type { Offer } from "../../lib/types/detailsTypes";
import { copyOfferToWallet } from "./offersFirestore";

interface OfferDetailsDialogProps {
  open: boolean;
  offer: Offer;
  onClose: () => void;
}

const OfferDetailsDialog: React.FC<OfferDetailsDialogProps> = ({
  open,
  offer,
  onClose,
}) => {
  const navigate = useNavigate();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );
  // Track whether we're currently adding the offer
  const [isAdding, setIsAdding] = useState(false);

  const handleSnackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleAddToWallet = async () => {
    try {
      setIsAdding(true); // disable button during the request

      // Assuming offer.id contains the offer ID.
      const result = await copyOfferToWallet(offer.id);

      setSnackbarMessage(result.message);
      setSnackbarSeverity(result.success ? "success" : "error");
      setSnackbarOpen(true);

      if (result.success) {
        // **Do NOT** close dialog here
        // Wait 1.5s, then navigate (user leaves this page so dialog is effectively gone)
        setTimeout(() => {
          navigate("/wallet/myoffers");
        }, 1500);
      } else {
        // If not successful, re-enable the button so user can try again
        setIsAdding(false);
      }
    } catch (error: any) {
      setSnackbarMessage(error.message || "An unexpected error occurred.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);

      // On error, re-enable so user can try again
      setIsAdding(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen={false}
        scroll="paper"
        PaperProps={{
          sx: {
            width: "100%",
            maxWidth: 400,
            maxHeight: "80vh",
            borderRadius: 4,
            position: "relative",
            m: "auto",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Dialog Header */}
        <DialogTitle sx={{ position: "relative" }}>
          {offer.storeName ? offer.storeName : "Offer Details"}
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              backgroundColor: "white",
              color: "black",
              borderRadius: "50%",
              boxShadow: 2,
              zIndex: 1,
              "&:hover": {
                backgroundColor: "#f0f0f0",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        {/* Dialog Content */}
        <DialogContent
          sx={{
            overflowY: "auto",
            flex: 1,
            px: 2,
            pt: 1,
            pb: 2,
          }}
        >
          {offer.imageUrl && (
            <Box
              component="img"
              src={offer.imageUrl}
              alt={offer.title}
              sx={{
                width: "100%",
                maxHeight: 160,
                objectFit: "contain",
                borderRadius: 2,
                mb: 2,
                mt: 1,
              }}
            />
          )}
          <Divider sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {offer.title}
          </Typography>
          <Typography variant="body1" paragraph>
            {offer.description}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {offer.singleUse ? "1 per Customer Only" : "Multiple uses allowed"}
          </Typography>
          {offer.validUntil && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              Expires: {offer.validUntil.toDate().toLocaleDateString()}
            </Typography>
          )}
          <Typography
            variant="body2"
            color="secondary"
            sx={{ mt: 1, fontWeight: 600 }}
          >
            Only few remaining!
          </Typography>
          {offer.termsAndConditions && (
            <Box sx={{ mt: 2, mb: 2 }}>
              <Accordion disableGutters>
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  aria-controls="terms-content"
                  id="terms-header"
                >
                  <Typography variant="subtitle2">Terms</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="textSecondary">
                    {offer.termsAndConditions}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Box>
          )}
        </DialogContent>

        {/* Dialog Actions */}
        <DialogActions
          sx={{
            flexDirection: "column",
            alignItems: "stretch",
            px: 2,
            pb: 2,
            pt: 1,
            position: "relative",
          }}
        >
          <Box
            sx={{
              backgroundColor: "white",
              p: 3,
              borderTop: "1px solid #e0e0e0",
            }}
          >
            <Button
              onClick={handleAddToWallet}
              variant="contained"
              disabled={isAdding} // disable button while adding
              sx={{
                fontWeight: 600,
                position: "absolute",
                bottom: 10,
                left: "50%",
                transform: "translateX(-50%)",
                width: "90%",
                borderRadius: "999px",
                boxShadow: 3,
                py: 1.5,
              }}
            >
              {isAdding ? "Adding..." : "Add to Wallet"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default OfferDetailsDialog;
